// src/services/authService.js

import { apiService } from './api'

class AuthService {
  async login(credentials) {
    try {
      const response = await apiService.post('/auth/admin/login', credentials)
      return response
    } catch (error) {
      // Preserve the original error response for role-based access handling
      if (error.response?.status === 403 && error.response?.data?.code === 'ROLE_NOT_ALLOWED') {
        throw error // Re-throw the original error to preserve the response structure
      }
      throw new Error(error.response?.data?.message || 'Login failed')
    }
  }

  async register(userData) {
    try {
      const response = await apiService.post('/auth/admin/register', userData)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Registration failed')
    }
  }

  async logout() {
    try {
      await apiService.post('/auth/admin/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  async getProfile() {
    try {
      const response = await apiService.get('/auth/admin/profile')
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch profile')
    }
  }

  async updateProfile(userData) {
    try {
      const response = await apiService.put('/auth/admin/profile', userData)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update profile')
    }
  }

  async changePassword(passwordData) {
    try {
      const response = await apiService.post('/auth/admin/change-password', passwordData)
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change password')
    }
  }

  async forgotPassword(email) {
    try {
      const response = await apiService.post('/auth/admin/forgot-password', { email })
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send reset email')
    }
  }

  async resetPassword(token, password) {
    try {
      const response = await apiService.post('/auth/admin/reset-password', {
        token,
        password
      })
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password')
    }
  }

  async verifyEmail(token) {
    try {
      const response = await apiService.post('/auth/admin/verify-email', { token })
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to verify email')
    }
  }

  async resendVerification(email) {
    try {
      const response = await apiService.post('/auth/admin/resend-verification', { email })
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to resend verification')
    }
  }

  // Check if token is valid
  async validateToken() {
    try {
      const response = await apiService.get('/auth/admin/validate')
      return response.valid
    } catch (error) {
      return false
    }
  }

  // Get user permissions
  async getPermissions() {
    try {
      const response = await apiService.get('/auth/permissions')
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch permissions')
    }
  }

  // Refresh token
  async refreshToken() {
    try {
      const response = await apiService.post('/auth/refresh')
      return response
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to refresh token')
    }
  }
}

export default new AuthService()
